/**
 * Hook for managing conversation persistence
 *
 * Provides CRUD operations for conversations and messages.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  api,
  ConversationResponse,
  ConversationWithMessages,
  ConversationMessageInput,
  ConversationMessageResponse,
} from '../api';

export interface UseConversationOptions {
  /** Arche ID for filtering conversations */
  archeId: string;
  /** Initial conversation ID to load */
  initialConversationId?: string | null;
  /** Callback when conversation changes */
  onConversationChange?: (conversationId: string | null) => void;
}

export interface UseConversationResult {
  /** Current conversation */
  conversation: ConversationWithMessages | null;
  /** List of user's conversations for this arche */
  conversations: ConversationResponse[];
  /** Whether data is loading */
  loading: boolean;
  /** Error if operation failed */
  error: Error | null;
  /** Create a new conversation */
  createConversation: (title?: string) => Promise<ConversationResponse | null>;
  /** Load an existing conversation */
  loadConversation: (conversationId: string) => Promise<void>;
  /** Add a message to current conversation */
  addMessage: (message: ConversationMessageInput) => Promise<ConversationMessageResponse[] | null>;
  /** Add multiple messages (for Multi-AI) */
  addMessages: (messages: ConversationMessageInput[]) => Promise<ConversationMessageResponse[] | null>;
  /** Update conversation title */
  updateTitle: (title: string) => Promise<void>;
  /** Archive current conversation */
  archiveConversation: () => Promise<void>;
  /** Delete current conversation */
  deleteConversation: () => Promise<void>;
  /** Delete a specific conversation by ID */
  deleteConversationById: (conversationId: string) => Promise<void>;
  /** Clear current conversation (starts fresh) */
  clearConversation: () => void;
  /** Refresh conversation list */
  refreshList: () => Promise<void>;
  /** Export conversation */
  exportConversation: (format: 'json' | 'markdown') => Promise<string | null>;
}

/**
 * Hook for managing conversation persistence
 */
export function useConversation(options: UseConversationOptions): UseConversationResult {
  const { archeId, initialConversationId, onConversationChange } = options;

  const [conversation, setConversation] = useState<ConversationWithMessages | null>(null);
  const [conversations, setConversations] = useState<ConversationResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load conversation list
  const loadConversationList = useCallback(async () => {
    if (!archeId) return;

    try {
      const result = await api.listConversations({ archeId, status: 'active' });
      if (isMountedRef.current) {
        setConversations(result.conversations);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    }
  }, [archeId]);

  // Load a specific conversation
  const loadConversation = useCallback(async (conversationId: string) => {
    setLoading(true);
    setError(null);

    try {
      const data = await api.getConversation(conversationId);
      if (isMountedRef.current) {
        setConversation(data);
        onConversationChange?.(conversationId);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [onConversationChange]);

  // Create a new conversation
  const createConversation = useCallback(async (title?: string): Promise<ConversationResponse | null> => {
    if (!archeId) return null;

    setLoading(true);
    setError(null);

    try {
      const data = await api.createConversation(archeId, title);
      if (isMountedRef.current) {
        // Create a conversation with messages structure
        const newConversation: ConversationWithMessages = {
          ...data,
          messages: [],
        };
        setConversation(newConversation);
        setConversations((prev) => [data, ...prev]);
        onConversationChange?.(data.conversationId);
      }
      return data;
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
      return null;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [archeId, onConversationChange]);

  // Add a single message
  // Helper to move a conversation to the top of the list (most recently updated)
  const moveConversationToTop = useCallback((conversationId: string) => {
    setConversations((prev) => {
      const index = prev.findIndex((c) => c.conversationId === conversationId);
      if (index <= 0) return prev; // Already at top or not found
      const conv = prev[index];
      const updated = {
        ...conv,
        updatedAt: new Date().toISOString(),
      };
      return [updated, ...prev.slice(0, index), ...prev.slice(index + 1)];
    });
  }, []);

  const addMessage = useCallback(async (
    message: ConversationMessageInput
  ): Promise<ConversationMessageResponse[] | null> => {
    if (!conversation) return null;

    try {
      const result = await api.addMessage(conversation.conversationId, message);
      if (isMountedRef.current && result.messages) {
        setConversation((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: [...prev.messages, ...result.messages],
            messageCount: prev.messageCount + result.messages.length,
          };
        });
        // Move this conversation to the top of the list
        moveConversationToTop(conversation.conversationId);
      }
      return result.messages;
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
      return null;
    }
  }, [conversation, moveConversationToTop]);

  // Add multiple messages (for Multi-AI)
  const addMessages = useCallback(async (
    messages: ConversationMessageInput[]
  ): Promise<ConversationMessageResponse[] | null> => {
    if (!conversation || messages.length === 0) return null;

    try {
      const result = await api.addMessages(conversation.conversationId, messages);
      if (isMountedRef.current && result.messages) {
        setConversation((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: [...prev.messages, ...result.messages],
            messageCount: prev.messageCount + result.messages.length,
          };
        });
        // Move this conversation to the top of the list
        moveConversationToTop(conversation.conversationId);
      }
      return result.messages;
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
      return null;
    }
  }, [conversation, moveConversationToTop]);

  // Update conversation title
  const updateTitle = useCallback(async (title: string) => {
    if (!conversation) return;

    try {
      const updated = await api.updateConversation(conversation.conversationId, { title });
      if (isMountedRef.current) {
        setConversation((prev) => prev ? { ...prev, title: updated.title } : prev);
        setConversations((prev) =>
          prev.map((c) => (c.conversationId === conversation.conversationId ? { ...c, title: updated.title } : c))
        );
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    }
  }, [conversation]);

  // Archive current conversation
  const archiveConversation = useCallback(async () => {
    if (!conversation) return;

    try {
      await api.updateConversation(conversation.conversationId, { status: 'archived' });
      if (isMountedRef.current) {
        setConversation(null);
        setConversations((prev) => prev.filter((c) => c.conversationId !== conversation.conversationId));
        onConversationChange?.(null);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    }
  }, [conversation, onConversationChange]);

  // Delete current conversation
  const deleteConversation = useCallback(async () => {
    if (!conversation) return;

    try {
      await api.deleteConversation(conversation.conversationId);
      if (isMountedRef.current) {
        setConversation(null);
        setConversations((prev) => prev.filter((c) => c.conversationId !== conversation.conversationId));
        onConversationChange?.(null);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    }
  }, [conversation, onConversationChange]);

  // Delete a specific conversation by ID (for non-active conversations)
  const deleteConversationById = useCallback(async (conversationId: string) => {
    try {
      await api.deleteConversation(conversationId);
      if (isMountedRef.current) {
        // Remove from local state immediately
        setConversations((prev) => prev.filter((c) => c.conversationId !== conversationId));
        // If this was the active conversation, clear it
        if (conversation?.conversationId === conversationId) {
          setConversation(null);
          onConversationChange?.(null);
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
    }
  }, [conversation, onConversationChange]);

  // Clear current conversation (local only)
  const clearConversation = useCallback(() => {
    setConversation(null);
    onConversationChange?.(null);
  }, [onConversationChange]);

  // Refresh conversation list
  const refreshList = useCallback(async () => {
    await loadConversationList();
  }, [loadConversationList]);

  // Export conversation
  const exportConversation = useCallback(async (format: 'json' | 'markdown'): Promise<string | null> => {
    if (!conversation) return null;

    try {
      return await api.exportConversation(conversation.conversationId, format);
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
      }
      return null;
    }
  }, [conversation]);

  // Load conversation list on mount
  useEffect(() => {
    loadConversationList();
  }, [loadConversationList]);

  // Load initial conversation if provided
  useEffect(() => {
    if (initialConversationId) {
      loadConversation(initialConversationId);
    }
  }, [initialConversationId, loadConversation]);

  return {
    conversation,
    conversations,
    loading,
    error,
    createConversation,
    loadConversation,
    addMessage,
    addMessages,
    updateTitle,
    archiveConversation,
    deleteConversation,
    deleteConversationById,
    clearConversation,
    refreshList,
    exportConversation,
  };
}

export default useConversation;
